import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getComputedStageMeta, formatRelativeTime } from './inquiriesShared';
import StageProgressPill from './StageProgressPill';
import UpcomingAppointmentsCard from '../components/UpcomingAppointmentsCard';
import './HomePage.css';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  return Number(value).toLocaleString('no-NO');
};

const toDateInputValue = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString().slice(0, 10);
};

const toIsoWithDaysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

const HomePage = () => {
  const { selectedCompanyId, permissions, userRole } = useAuth();
  const companyId = selectedCompanyId;
  const hasSystemAccess = Boolean(
    permissions?.isOwner
    || (Array.isArray(permissions?.permissionKeys) && permissions.permissionKeys.length > 0),
  );
  const [overview, setOverview] = useState(null);
  const [inboxSummary, setInboxSummary] = useState(null);
  const [inquiryOverview, setInquiryOverview] = useState(null);
  const [assignedInquiries, setAssignedInquiries] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const initial = new Date(now);
    initial.setDate(initial.getDate() - 6);
    return toDateInputValue(initial);
  });
  const [toDate] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return toDateInputValue(now);
  });

  useEffect(() => {
    if (!companyId || !hasSystemAccess) {
      setOverview(null);
      setInboxSummary(null);
      setInquiryOverview(null);
      setAssignedInquiries([]);
      setUpcomingEvents([]);
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    async function load() {
      setLoading(true);
      try {
        const params = {
          from: fromDate || undefined,
          to: toDate || undefined,
        };
        const [overviewResult, inboxSummaryResult] = await Promise.allSettled([
          DashboardApi.getOverview(companyId, params),
          permissions?.canViewInbox
            ? DashboardApi.getCompanyInboxSummary(companyId, { compact: 1 })
            : Promise.resolve(null),
        ]);
        const canLoadInquiryOverview = Boolean(permissions?.canViewInquiryOverview);
        const assignedResult = permissions?.canViewInbox
          ? await Promise.allSettled([
            canLoadInquiryOverview
              ? DashboardApi.getCompanyInboxOverview(companyId, { period: 'last30' })
              : Promise.resolve(null),
            !canLoadInquiryOverview
              ? DashboardApi.getCompanyInbox(companyId, { assignedAccountId: 'me', limit: 5, offset: 0 })
              : Promise.resolve(null),
            DashboardApi.getCompanyInboxCalendar(companyId, {
              from: new Date().toISOString(),
              to: toIsoWithDaysFromNow(30),
              assigned: 'mine_or_unassigned',
              limit: 5,
            }),
          ])
          : [];
        if (!isMounted) return;
        if (overviewResult.status !== 'fulfilled') {
          throw overviewResult.reason;
        }
        setOverview(overviewResult.value.overview);
        if (inboxSummaryResult.status === 'fulfilled') {
          setInboxSummary(inboxSummaryResult.value);
        } else {
          setInboxSummary(null);
        }
        const inquiryOverviewResult = assignedResult[0];
        const assignedListResult = assignedResult[1];
        const upcomingEventsResult = assignedResult[2];
        if (inquiryOverviewResult?.status === 'fulfilled' && inquiryOverviewResult.value) {
          setInquiryOverview(inquiryOverviewResult.value);
          setAssignedInquiries(Array.isArray(inquiryOverviewResult.value.mine) ? inquiryOverviewResult.value.mine : []);
        } else if (assignedListResult?.status === 'fulfilled' && assignedListResult.value) {
          setInquiryOverview(null);
          setAssignedInquiries(Array.isArray(assignedListResult.value.inquiries) ? assignedListResult.value.inquiries : []);
        } else {
          setInquiryOverview(null);
          setAssignedInquiries([]);
        }
        if (upcomingEventsResult?.status === 'fulfilled' && upcomingEventsResult.value) {
          setUpcomingEvents(Array.isArray(upcomingEventsResult.value.events) ? upcomingEventsResult.value.events : []);
        } else {
          setUpcomingEvents([]);
        }
        setError(null);
      } catch (err) {
        if (!isMounted) return;
        setInboxSummary(null);
        setInquiryOverview(null);
        setAssignedInquiries([]);
        setUpcomingEvents([]);
        setError(err.message || 'Klarte ikke hente dashbordet.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [companyId, fromDate, hasSystemAccess, permissions?.canViewInbox, permissions?.canViewInquiryOverview, toDate]);

  const kpiCards = useMemo(() => {
    if (!overview) {
      return [
        { title: 'Samtaler denne uken', value: '—', hint: 'Siste 7 dager' },
        { title: 'Meldinger denne uken', value: '—', hint: 'Siste 7 dager' },
        { title: 'Aktive bots', value: '—', hint: 'Aktive nå' },
        { title: 'Nye henvendelser', value: '—', hint: permissions?.canViewInbox ? 'Nye i innboksen' : null },
      ];
    }
    const newInquiryCount = Number(inboxSummary?.totals?.new || 0);
    const cards = [
      {
        title: 'Samtaler denne uken',
        value: formatNumber(overview.kpis.conversations),
        hint: 'Siste 7 dager',
      },
      {
        title: 'Meldinger denne uken',
        value: formatNumber(overview.kpis.messages),
        hint: 'Siste 7 dager',
      },
      {
        title: 'Aktive bots',
        value: formatNumber(overview.kpis.activeBots),
        hint: 'Aktive nå',
      },
      {
        title: 'Nye henvendelser',
        value: permissions?.canViewInbox ? formatNumber(newInquiryCount) : '—',
        hint: permissions?.canViewInbox ? 'Nye i innboksen' : null,
      },
    ];

    // Add average response time if available
    if (overview.kpis.avgResponseTime) {
      cards.push({
        title: 'Gj.snitt responstid',
        value: overview.kpis.avgResponseTime,
        hint: 'Siste 7 dager',
      });
    }

    // Add flow runs if any
    if (overview.kpis.totalFlowRuns > 0) {
      cards.push({
        title: 'Flyter kjørt denne uken',
        value: formatNumber(overview.kpis.flowsCompleted),
        hint: overview.kpis.flowsFailed > 0 ? `${overview.kpis.flowsFailed} feilet` : 'Ingen feil',
        hintTone: overview.kpis.flowsFailed > 0 ? 'danger' : null,
      });
    }

    return cards;
  }, [inboxSummary, overview, permissions?.canViewInbox]);

  // Navigation cards based on permissions
  const navCards = useMemo(() => {
    const cards = [];
    const resolvedPermissions = permissions || {};

    if (resolvedPermissions.canViewAgent) {
      cards.push({
        to: '/agent',
        icon: '💬',
        title: 'Agent-panel',
        description: 'Håndter live chat-samtaler og gi kundestøtte i sanntid.',
        color: '#8b5cf6',
      });
    }

    if (resolvedPermissions.canViewInquiryOverview || resolvedPermissions.canViewInbox) {
      const total = Number(inquiryOverview?.totals?.total ?? inboxSummary?.totals?.total ?? 0);
      cards.push({
        to: resolvedPermissions.canViewInquiryOverview ? '/henvendelser' : '/henvendelser/innboks',
        icon: '!',
        title: 'Henvendelser',
        description: total > 0
          ? `${formatNumber(total)} henvendelser innenfor tilgangen din.`
          : 'Åpne henvendelsesoversikt og inbox.',
        color: 'var(--accent-cyan)',
      });
    }

    if (resolvedPermissions.canViewChatHistory) {
      cards.push({
        to: '/chat-historikk',
        icon: 'C',
        title: 'Chat-historikk',
        description: 'Se chat-historikk og analyser samtaledata.',
        color: '#3b82f6',
      });
    }

    if (resolvedPermissions.canViewBots) {
      cards.push({
        to: '/bots',
        icon: '🤖',
        title: 'Bots',
        description: 'Administrer dine chatbots, flyter og kunnskapsbase.',
        color: '#10b981',
      });
    }

    if (resolvedPermissions.canViewCompany) {
      cards.push({
        to: '/selskap',
        icon: '🏢',
        title: 'Selskap',
        description: 'Administrer team, abonnement og selskapinnstillinger.',
        color: '#f59e0b',
      });
    }

    return cards;
  }, [inboxSummary?.totals?.total, inquiryOverview?.totals?.total, permissions]);

  // Get role-specific welcome message
  const getRoleMessage = () => {
    switch (userRole) {
      case 'owner':
        return 'Du har full tilgang til alle funksjoner.';
      default:
        return hasSystemAccess
          ? 'Tilgangene dine styres av gruppene du er medlem av.'
          : 'Ta kontakt med din administrator for å få tilganger.';
    }
  };

  const assignedCases = useMemo(
    () => (Array.isArray(assignedInquiries) ? assignedInquiries.slice(0, 5) : []),
    [assignedInquiries],
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Hjem</h1>
          <p className="page-subtitle">
            {getRoleMessage()}
          </p>
        </div>
      </div>

      {error && (
        <div className="card">
          <p>⚠️ {error}</p>
        </div>
      )}

      {!hasSystemAccess && (
        <div className="card">
          <h2>Ingen tilganger</h2>
          <p>Ta kontakt med din administrator for å få tilganger.</p>
        </div>
      )}

      {/* KPI Metrics */}
      {hasSystemAccess && (
      <section className="card-grid">
        {kpiCards.map((card) => (
          <article key={card.title} className="card kpi-card">
            <span className="card-title">{card.title}</span>
            <p className="card-value">{loading ? '—' : card.value}</p>
            {!loading && card.hint && (
              <p className={`kpi-card-hint${card.hintTone === 'danger' ? ' is-danger' : ''}`}>
                {card.hint}
              </p>
            )}
          </article>
        ))}
      </section>
      )}

      {hasSystemAccess && permissions?.canViewInbox && !loading && upcomingEvents.length > 0 && (
        <section className="home-upcoming-section">
          <UpcomingAppointmentsCard events={upcomingEvents} />
        </section>
      )}

      {/* Navigation Cards */}
      {navCards.length > 0 && (
        <section className="nav-cards-section">
          <h2 className="section-title">Hurtigtilgang</h2>
          <div className="nav-cards-grid">
            {navCards.map((card) => (
              <Link
                key={card.to}
                to={card.to}
                className="nav-card"
                style={{ '--card-accent': card.color }}
              >
                <span className="nav-card-icon">{card.icon}</span>
                <div className="nav-card-content">
                  <h3 className="nav-card-title">{card.title}</h3>
                  <p className="nav-card-description">{card.description}</p>
              </div>
                <span className="nav-card-arrow">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {hasSystemAccess && permissions?.canViewInbox && assignedCases.length > 0 && (
        <section className="home-assigned-section">
          <div className="card home-assigned-card">
            <header className="card-header home-assigned-header">
              <div>
                <span className="card-title">Mine saker</span>
                <p className="home-assigned-subtitle">Åpne henvendelser som er tildelt deg.</p>
              </div>
              <Link to="/henvendelser/innboks?assignedAccountId=me" className="button button-secondary button-small">
                Se alle
              </Link>
            </header>
            <div className="home-assigned-list">
              {assignedCases.map((inquiry) => {
                const stageMeta = getComputedStageMeta(inquiry.stage, inquiry.progression);
                const activityAt = inquiry.last_activity_at || inquiry.submitted_at || inquiry.created_at;
                const title = inquiry.display_name || inquiry.name || `Henvendelse #${inquiry.id}`;
                const subtitle = inquiry.ai_summary || inquiry.summary || inquiry.product || 'Ingen oppsummering ennå';
                return (
                  <Link key={inquiry.id} to={`/henvendelser/${inquiry.id}`} className="home-assigned-row">
                    <span className="home-assigned-main">
                      <strong>{title}</strong>
                      <small>{subtitle}</small>
                    </span>
                    <span className="home-assigned-side">
                      <StageProgressPill
                        label={stageMeta.label}
                        progression={inquiry.progression}
                        className={`workflow-badge ${stageMeta.className}`}
                      />
                      <small>{activityAt ? formatRelativeTime(activityAt) : 'Ukjent tidspunkt'}</small>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default HomePage;
